import React, { useEffect, useRef, useState } from 'react';
import emailjs from 'emailjs-com';
import './ContactForm.css';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    from_name: '',
    from_email: '',
    subject: '',
    message: ''
  });
  
  const [focused, setFocused] = useState({
    from_name: false,
    from_email: false,
    subject: false,
    message: false
  });
  
  const formRef = useRef(null);
  const nameRef = useRef(null);
  const emailRef = useRef(null);
  const subjectRef = useRef(null);
  const messageRef = useRef(null);
  const submitRef = useRef(null);
  const titleRef = useRef(null);
  const subtitleRef = useRef(null);
  
  // Refs for the form groups that contain the inputs
  const nameGroupRef = useRef(null);
  const emailGroupRef = useRef(null);
  const subjectGroupRef = useRef(null);
  const messageGroupRef = useRef(null);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };
  
  const handleFocus = (e) => {
    const { name } = e.target;
    setFocused(prevState => ({
      ...prevState,
      [name]: true
    }));
  };
  
  const handleBlur = (e) => {
    const { name } = e.target;
    setFocused(prevState => ({
      ...prevState,
      [name]: false
    }));
  };
  
  const sendEmail = (e) => {
    e.preventDefault();
    
    // Add animation class to form on submit
    formRef.current.classList.add('form-submitted');
    
    emailjs.sendForm('service_wl7bnjf', 'template_ji4tg5i', formRef.current, '9A5zkFLWhjs2Ycpjp')
      .then((result) => {
        console.log(result.text);
        
        // Reset form after successful submission
        e.target.reset();
        setFormData({
          from_name: '',
          from_email: '',
          subject: '',
          message: ''
        });
        
        // Show success message
        alert('Thank you for your message! We will get back to you soon.');
        
        // Remove animation class
        setTimeout(() => {
          formRef.current.classList.remove('form-submitted');
        }, 800);
        
      }, (error) => {
        console.log(error.text);
        alert('Oops! Something went wrong. Please try again.');
        
        // Remove animation class even on error
        setTimeout(() => {
          formRef.current.classList.remove('form-submitted');
        }, 800);
      });
  };
  
  useEffect(() => {
    // Intersection Observer for scroll animations
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);
    
    // Observe all animated elements
    [
      titleRef.current,
      subtitleRef.current,
      nameGroupRef.current,
      emailGroupRef.current,
      subjectGroupRef.current,
      messageGroupRef.current,
      submitRef.current
    ].forEach(el => {
      if (el) observer.observe(el);
    });
    
    return () => {
      observer.disconnect();
    };
  }, []);
  
  return (
    <section className="contact-section">
      <div className="contact-container">
        <div className="contact-text">
          <h2 ref={titleRef} className="contact-title animate-element">
            <span className="highlight">Get</span> In Touch
          </h2>
          <p ref={subtitleRef} className="contact-subtitle animate-element">
            Have a project in mind? Let's bring your ideas to life with our cutting-edge technology solutions.
          </p>
        </div>
        
        <form ref={formRef} className="contact-form" onSubmit={sendEmail}>
          <div 
            className={`form-group animate-element ${focused.from_name || formData.from_name ? 'focused' : ''}`} 
            ref={nameGroupRef}
          >
            <label htmlFor="from_name">Name</label>
            <input
              type="text"
              id="from_name"
              name="from_name"
              value={formData.from_name}
              onChange={handleChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              required
              placeholder="Your Name"
            />
            <div className="input-highlight"></div>
          </div>
          
          <div 
            className={`form-group animate-element ${focused.from_email || formData.from_email ? 'focused' : ''}`} 
            ref={emailGroupRef}
          >
            <label htmlFor="from_email">Email</label>
            <input
              type="email"
              id="from_email"
              name="from_email"
              value={formData.from_email}
              onChange={handleChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              required
              placeholder="Your Email"
            />
            <div className="input-highlight"></div>
          </div>
          
          <div 
            className={`form-group animate-element ${focused.subject || formData.subject ? 'focused' : ''}`} 
            ref={subjectGroupRef}
          >
            <label htmlFor="subject">Subject</label>
            <input
              type="text"
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              required
              placeholder="Subject"
            />
            <div className="input-highlight"></div>
          </div>
          
          <div 
            className={`form-group animate-element ${focused.message || formData.message ? 'focused' : ''}`} 
            ref={messageGroupRef}
          >
            <label htmlFor="message">Message</label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              required
              placeholder="Your Message"
              rows="5"
            />
            <div className="input-highlight"></div>
          </div>
          
          <button 
            type="submit" 
            className="submit-btn animate-element" 
            ref={submitRef}
          >
            <span className="btn-text">Send Message</span>
            <span className="btn-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13"></line>
                <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
              </svg>
            </span>
          </button>
        </form>
      </div>

      <style>
        {`
          /* Base animation styles */
          .animate-element {
            opacity: 0;
            transform: translateY(30px);
            transition: opacity 0.8s ease, transform 0.8s ease;
          }
          
          .animate-element.animate-in {
            opacity: 1;
            transform: translateY(0);
          }
          
          /* Staggered animation delay for form groups */
          .form-group.animate-element:nth-of-type(1) {
            transition-delay: 0.1s;
          }
          
          .form-group.animate-element:nth-of-type(2) {
            transition-delay: 0.2s;
          }
          
          .form-group.animate-element:nth-of-type(3) {
            transition-delay: 0.3s;
          }
          
          .form-group.animate-element:nth-of-type(4) {
            transition-delay: 0.4s;
          }
          
          /* Submit button animation */
          .submit-btn.animate-element {
            transition-delay: 0.5s;
          }
          
          /* Title and subtitle animations */
          .contact-title.animate-element {
            transition-delay: 0s;
          }
          
          .contact-subtitle.animate-element {
            transition-delay: 0.1s;
          }
          
          /* Form submission animation */
          @keyframes formSuccess {
            0% { transform: translateY(0); }
            50% { transform: translateY(-10px); }
            100% { transform: translateY(0); }
          }
          
          .form-submitted {
            animation: formSuccess 0.8s ease;
          }
          
          /* Input focus animations */
          .input-highlight {
            position: absolute;
            bottom: 0;
            left: 0;
            height: 2px;
            width: 0;
            background-color: #e32961;
            transition: width 0.3s ease;
          }
          
          .form-group.focused .input-highlight {
            width: 100%;
          }
          
          /* Button hover effect */
          .submit-btn {
            overflow: hidden;
            position: relative;
          }
          
          .btn-icon {
            position: absolute;
            right: -30px;
            top: 50%;
            transform: translateY(-50%);
            opacity: 0;
            transition: right 0.3s ease, opacity 0.3s ease;
          }
          
          .submit-btn:hover .btn-text {
            transform: translateX(-10px);
          }
          
          .submit-btn:hover .btn-icon {
            right: 20px;
            opacity: 1;
          }
          
          .btn-text {
            display: inline-block;
            transition: transform 0.3s ease;
          }
          
          /* Highlight effect for title */
          .highlight {
            color: #e32961;
            position: relative;
            display: inline-block;
          }
          
          .highlight:after {
            content: '';
            position: absolute;
            width: 100%;
            height: 30%;
            background-color: rgba(227, 41, 97, 0.2);
            bottom: 0;
            left: 0;
            z-index: -1;
            transition: height 0.3s ease;
          }
          
          .contact-title:hover .highlight:after {
            height: 50%;
          }
        `}
      </style>
    </section>
  );
};

export default ContactForm; 